import React, { useState, useContext } from 'react';
import { HrButton } from '@components/button/button';
import { Input } from '@components/form/input';
import { InputPassword } from '@components/form/input/input';
import {
  HrActionText,
  HrButtons,
  HrFlexContainer,
  HrParagraph,
  HrPrimary,
  HrRed,
  HrTitleH5,
  HrWhite
} from '@components/styles/hr-styles';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getLoginPortalExternal } from '../../services/api';
import useStore from '../../store/useStore';
import { Modal } from '../modal';
import { DisplaySettingsContext } from '../../store/display-settings';

const Logo = styled.img`
  width: 100%;
  height: 40px;

  @media (max-width: 768px) {
    width: 70%;
    height: 40px;
  }
`;

const Flex = styled(HrFlexContainer)`
  padding: 48px 32px;

  @media (max-width: 425px) {
    padding: 48px 16px;
  }
`;

export const ModalLogin = ({ setShowModal, dataModal }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isError, setIsError] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const { getDataPostulant } = useStore();
  const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);

  const nameBumeranButton = getDataPostulant.postulationOptions?.bumeran.name || t('general.hiringroom.bumeran');
  const nameZonajobsButton = getDataPostulant.postulationOptions?.zonaJobs.name || t('general.hiringroom.zonajobs');
  const imgSrcBumeran = `../../../../../assets/img/logos/imagotipo/${darkMode ? 'bumeran-white.svg' : `${nameBumeranButton.toLowerCase()}-blue.svg`}`;
  const imgSrcZonajobs = `../../../../../assets/img/logos/imagotipo/${nameZonajobsButton.toLowerCase()}-orange.svg`;

  const { handleSubmit: handleSubmitLogin, register: registerLogin, formState: formStateLogin } = useForm({
    mode: 'onTouched'
  });

  const loginPortalExternal = useMutation(getLoginPortalExternal, {
    onSuccess: (data) => {
      setIsLoading(false);
      if (data.data.result === 'success') {
        setShowModal((prev) => {
          return {
            ...prev,
            show: false,
            autoLogin: true,
            token: data.data.response
          };
        });
        navigate({
          pathname: location.pathname,
          replace: true,
          search: location.search,
          state: { currentStep: 2 },
          hash: `#step${2}`
        });
      }

      if (data.data.result === 'error') {
        let message = t(`postulant.error.import.${data.data.response}`);
        if (message === `postulant.error.import.${data.data.response}`) message = t(`postulant.error.import.support`);

        setIsError(message);
      }
    },
    onError: (error) => {
      setIsLoading(false);
    }
  });

  const _handleSubmitLogin = (data, e) => {
    e.preventDefault();
    setIsError(null);
    setIsLoading(true);
    data.apply_with = dataModal.apply_with;
    loginPortalExternal.mutate(data);
  };

  return (
    <Modal
      setShowModal={setShowModal}
      handleClosedModal={() => {
        setShowModal((prev) => {
          return { ...prev, show: false };
        });
      }}
    >
      <Flex flexDirection='column' width='auto'>
        {dataModal.portal === 'bumeran' && <Logo src={imgSrcBumeran} alt={`Logo de ${nameBumeranButton}`} />}
        {dataModal.portal === 'zonajobs' && <Logo src={imgSrcZonajobs} alt={`Logo de ${nameZonajobsButton}`} />}

        <HrTitleH5 textAlign='center' padding='16px 0 0 0' fontSizeSetting={fontSizeSetting} color={darkMode ? 'white' : ''}>
          {t('postulant.modal.login.text_part_one')}
          {dataModal.portal === 'bumeran' && nameBumeranButton}
          {dataModal.portal === 'zonajobs' && nameZonajobsButton}
          <br />
          {t('postulant.modal.login.text_part_two')}
        </HrTitleH5>

        <form
          key='formLogin'
          name='formLogin'
          onSubmit={handleSubmitLogin(_handleSubmitLogin)}
          style={{ width: '100%' }}
        >
          <HrFlexContainer flexDirection='column'>
            <Input
              title={t('postulant.mail')}
              placeholder={t('postulant.placeholder.mail')}
              name='user'
              type='text'
              register={registerLogin}
              formState={formStateLogin}
              validate={{
                required: true,
                mail: true
              }}
            />
            <InputPassword
              title={t('postulant.password')}
              placeholder={t('postulant.placeholder.password')}
              name='password'
              type='password'
              register={registerLogin}
              formState={formStateLogin}
              validate={{
                required: true
              }}
            />
            {isError && (
              <HrParagraph textAlign='center' color={HrRed}>
                <span className='hr-Alert' style={{ color: HrRed, marginRight: '4px' }} />
                {isError}
              </HrParagraph>
            )}
          </HrFlexContainer>
          <HrFlexContainer width='auto' gap='8px' padding='16px 0' justifyContent='center'>
            <HrButtons
              type='button'
              category='cancel'
              size='medium'
              style={{fontSize: `${14 + fontSizeSetting || 0}px`}}
              padding='0 32px'
              onClick={() =>
                setShowModal((prev) => {
                  return { ...prev, show: false };
                })
              }
            >
              {t('general.button.cancel')}
            </HrButtons>
            <HrButton
              type='submit'
              name='submit'
              category='primary'
              size='medium'
              fontSize={`${14 + fontSizeSetting || 0}px`}
              padding='0 32px'
              text={t('general.button.log_in')}
              iconColor={HrWhite}
              loading={isLoading}
              disabled={isLoading}
            />
          </HrFlexContainer>
        </form>
        <HrFlexContainer justifyContent='center' gap='4px'>
          <HrParagraph fontSizeSetting={fontSizeSetting} color={darkMode ? 'white' : ''}>
            {`
            ¿No tienes usuario de
            ${
              (dataModal.portal === 'bumeran' && nameBumeranButton) ||
              (dataModal.portal === 'zonajobs' && nameZonajobsButton)
            }?`}
          </HrParagraph>
          <a href={dataModal.url} style={{ textDecoration: 'none' }} rel='noreferrer' target='_blank'>
            <HrActionText fontSizeSetting={fontSizeSetting} color={HrPrimary}>{t('postulant.modal.login.create_account')}</HrActionText>
          </a>
        </HrFlexContainer>
      </Flex>
    </Modal>
  );
};
